<template>
  <section class="relationship-history-panel" aria-labelledby="relationship-history-title">
    <header class="relationship-history-panel__header">
      <div>
        <v-icon small color="primary">mdi-history</v-icon>
        <strong id="relationship-history-title">Run history</strong>
      </div>
      <v-chip v-if="loadedLabel" x-small outlined>{{ loadedLabel }}</v-chip>
    </header>

    <div
      v-if="loading && !runs.length"
      class="relationship-history-panel__list"
      role="status"
      aria-live="polite"
      aria-busy="true">
      <span class="ds-sr-only">Loading run history</span>
      <div
        v-for="index in 3"
        :key="index"
        class="relationship-history-panel__item relationship-history-panel__item--skeleton"
        aria-hidden="true">
        <span class="relationship-history-panel__bone relationship-history-panel__bone--icon"></span>
        <div class="relationship-history-panel__content">
          <span class="relationship-history-panel__bone relationship-history-panel__bone--title"></span>
          <span class="relationship-history-panel__bone relationship-history-panel__bone--meta"></span>
        </div>
        <span class="relationship-history-panel__bone relationship-history-panel__bone--chip"></span>
      </div>
    </div>

    <div
      v-else-if="problemState"
      class="relationship-history-panel__problem"
      :class="{ 'relationship-history-panel__problem--failure': problemState.failure }"
      role="alert">
      <v-icon small>{{ problemState.icon }}</v-icon>
      <span>{{ problemState.message }}</span>
      <v-btn
        v-if="problemState.canRetry"
        small
        outlined
        color="primary"
        :loading="loading"
        @click="$emit('retry')">Try again</v-btn>
    </div>

    <div v-else-if="runs.length" class="relationship-history-panel__list">
      <div v-if="problem" class="relationship-history-panel__problem" role="alert">
        <v-icon small color="error">mdi-alert-circle-outline</v-icon>
        <span>Older runs could not be loaded.</span>
        <v-btn small outlined color="primary" :loading="loading" @click="$emit('retry')">Reload</v-btn>
      </div>
      <button
        v-for="run in runs"
        :key="run.id"
        type="button"
        class="relationship-history-panel__item"
        :aria-label="runAriaLabel(run)"
        @click="$emit('open', run)">
        <div class="relationship-history-panel__icon" :class="`relationship-history-panel__icon--${runMeta(run).color}`">
          <v-icon x-small>{{ runMeta(run).icon }}</v-icon>
        </div>
        <div class="relationship-history-panel__content">
          <div class="relationship-history-panel__title">
            <strong>{{ runName(run) }}</strong>
            <span class="relationship-history-panel__status">{{ runMeta(run).label }}</span>
          </div>
          <div class="relationship-history-panel__meta">
            <span>{{ scopeSummary(run) }}</span>
            <span>{{ formatDate(run.createdAt) }}</span>
            <span v-if="triggerLabel(run)">{{ triggerLabel(run) }}</span>
            <span v-if="expiryLabel(run)">{{ expiryLabel(run) }}</span>
            <span v-if="run.targetRedacted">
              <v-icon x-small>mdi-eye-off-outline</v-icon>Target details restricted
            </span>
          </div>
        </div>
        <div class="relationship-history-panel__counts">
          <v-chip
            v-for="entry in countEntries(run)"
            :key="entry.key"
            x-small
            outlined
            :color="entry.color">
            {{ entry.count }} {{ entry.label }}
          </v-chip>
        </div>
        <v-icon x-small color="grey">mdi-chevron-right</v-icon>
      </button>
      <v-btn v-if="cursor" block text color="primary" :loading="loading" @click="$emit('load-more')">
        <v-icon left small>mdi-chevron-down</v-icon>Load older runs
      </v-btn>
    </div>

    <div v-else-if="availability === 'ready'" class="relationship-history-panel__empty">
      <v-icon color="grey">mdi-history</v-icon>
      <span>No persisted runs yet. Previewed and completed work will appear here for audit and recovery.</span>
    </div>
  </section>
</template>

<script>
import {
  formatRelationshipTimestamp,
  relationshipRunCountEntries,
  relationshipRunScopeLabel,
  relationshipRunTriggerLabel,
  runStatusMeta,
} from '../../config/relationshipUi';

export default {
  name: 'RelationshipRunHistoryPanel',
  props: {
    runs: { type: Array, default: () => [] },
    availability: { type: String, default: 'unknown' },
    cursor: { type: String, default: null },
    total: { type: Number, default: null },
    loading: { type: Boolean, default: false },
    problem: { type: Object, default: null },
  },
  computed: {
    // One statement of what is wrong, differentiating a denial and a missing capability from a
    // failure. Only a failure is worth retrying.
    problemState() {
      if (this.runs.length) return null;
      if (this.availability === 'forbidden') {
        return {
          icon: 'mdi-lock-outline',
          message: 'You do not have access to the run history for this register.',
          canRetry: false,
        };
      }
      if (this.availability === 'unavailable') {
        return {
          icon: 'mdi-api-off',
          message: 'Run history is not available in this deployment.',
          canRetry: false,
        };
      }
      if (!this.problem) return null;
      return {
        icon: 'mdi-alert-circle-outline',
        failure: true,
        message: 'Run history could not be loaded. The server did not respond.',
        canRetry: true,
      };
    },
    loadedLabel() {
      // The panel body states the condition; a header chip repeating it is a second voice.
      if (this.problemState) return '';
      // Before the first answer there is nothing loaded to count, and "0 loaded" would read as an
      // answer rather than as an absence of one. While loading, the skeleton rows are the single
      // indicator; a second "Loading" chip in the header would just be a competing one.
      if (['unknown', 'loading'].includes(this.availability)) return '';
      return this.total === null
        ? `${this.runs.length} loaded`
        : `${this.runs.length} of ${this.total}`;
    },
  },
  methods: {
    // Every call site here holds a run, not a status string. Passing the run object straight to
    // runStatusMeta silently matched no key, so the list rendered the "Status unavailable"
    // fallback for every row regardless of what the run actually did.
    runMeta(run) {
      return runStatusMeta(run?.status);
    },
    countEntries: relationshipRunCountEntries,
    formatDate: formatRelationshipTimestamp,
    triggerLabel: relationshipRunTriggerLabel,
    runName(run) {
      return run?.configSnapshot?.name || 'Historical relationship';
    },
    scopeSummary(run) {
      const label = relationshipRunScopeLabel(run);
      const count = Number(run?.scopeCount || 0);
      return count ? `${label} - ${count} ${count === 1 ? 'row' : 'rows'}` : label;
    },
    // Only an unconfirmed run still has a deadline. Once it has run, been cancelled, or lapsed,
    // the same timestamp is history and must not be presented as a countdown.
    expiryLabel(run) {
      if (run?.status !== 'awaitingConfirmation' || !run?.expiresAt) return '';
      const expiresAt = new Date(run.expiresAt);
      if (Number.isNaN(expiresAt.getTime())) return '';
      const formatted = formatRelationshipTimestamp(run.expiresAt);
      return expiresAt.getTime() <= Date.now() ? `Expired ${formatted}` : `Expires ${formatted}`;
    },
    runAriaLabel(run) {
      const counts = this.countEntries(run)
        .map((entry) => `${entry.count} ${entry.label}`)
        .join(', ');
      const facts = [
        this.scopeSummary(run),
        this.formatDate(run?.createdAt),
        this.triggerLabel(run),
        this.expiryLabel(run),
        counts,
      ].filter(Boolean).join(', ');
      return `Open ${this.runMeta(run).label} run for ${this.runName(run)}. ${facts}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.relationship-history-panel { overflow: hidden; border: 1px solid var(--ds-border-default); border-radius: var(--ds-radius-md); background: var(--v-bg2-base); }
.relationship-history-panel__header { display: flex; align-items: center; justify-content: space-between; gap: var(--ds-space-sm); padding: var(--ds-space-md); border-bottom: 1px solid var(--ds-border-subtle); }
.relationship-history-panel__header > div { display: flex; align-items: center; gap: var(--ds-space-xs); }
.relationship-history-panel__list { max-height: 420px; overflow-y: auto; }
.relationship-history-panel__item { display: flex; width: 100%; min-height: 56px; align-items: center; gap: var(--ds-space-sm); padding: var(--ds-space-sm) var(--ds-space-md); border: 0; border-bottom: 1px solid var(--ds-border-subtle); color: inherit; text-align: left; background: transparent; cursor: pointer; }
.relationship-history-panel__item:hover { background: var(--v-bg3-base); }
.relationship-history-panel__item:focus-visible { position: relative; z-index: 1; outline: var(--ds-focus-ring-width) solid var(--ds-focus-ring-color); outline-offset: calc(var(--ds-focus-ring-offset) * -1); background: var(--v-bg3-base); }
.relationship-history-panel__icon { display: flex; align-items: center; justify-content: center; width: 26px; height: 26px; flex: 0 0 26px; border-radius: 50%; background: var(--v-bg3-base); }
.relationship-history-panel__icon--success { color: var(--v-success-base); }
.relationship-history-panel__icon--warning { color: var(--v-warning-base); }
.relationship-history-panel__icon--error { color: var(--v-error-base); }
.relationship-history-panel__icon--info { color: var(--v-info-base); }
.relationship-history-panel__content { display: flex; min-width: 0; flex: 1; flex-direction: column; gap: 2px; }
.relationship-history-panel__title { display: flex; align-items: baseline; min-width: 0; gap: var(--ds-space-sm); }
.relationship-history-panel__title strong { overflow: hidden; flex: 0 1 auto; text-overflow: ellipsis; white-space: nowrap; font-size: var(--ds-font-body-sm); }
.relationship-history-panel__status { flex: 0 0 auto; color: var(--v-text-base); font-size: var(--ds-font-caption); font-weight: 600; }
.relationship-history-panel__meta { display: flex; align-items: center; flex-wrap: wrap; min-width: 0; gap: var(--ds-space-xs) var(--ds-space-sm); color: var(--relationship-muted, #59635f); font-size: var(--ds-font-caption); }
.relationship-history-panel__meta > span { display: inline-flex; align-items: center; gap: 3px; }
.relationship-history-panel__meta > span:not(:last-child)::after { width: 3px; height: 3px; flex: 0 0 3px; border-radius: 50%; background: var(--ds-border-strong); content: ''; }
.relationship-history-panel__counts { display: flex; align-items: center; justify-content: flex-end; flex: 0 1 auto; flex-wrap: wrap; gap: var(--ds-space-xs); max-width: 45%; }
/* Skeleton rows reuse the loaded row geometry so arriving content does not move anything.
   Bones stay static: design-system.md bans shimmer on loading content areas. */
.relationship-history-panel__item--skeleton { cursor: default; }
.relationship-history-panel__item--skeleton:hover { background: transparent; }
.relationship-history-panel__bone { display: block; flex: 0 0 auto; border-radius: var(--ds-radius-sm); background: var(--relationship-skeleton-bone, rgba(41, 49, 46, 0.1)); }
.relationship-history-panel__bone--icon { width: 26px; height: 26px; border-radius: 50%; }
.relationship-history-panel__bone--title { width: 210px; max-width: 55%; height: 13px; }
.relationship-history-panel__bone--meta { width: 320px; max-width: 85%; height: 11px; }
.relationship-history-panel__bone--chip { width: 96px; height: 18px; border-radius: var(--ds-radius-full); }
.relationship-history-panel__empty { display: flex; align-items: center; flex-direction: column; gap: var(--ds-space-xs); padding: var(--ds-space-lg); color: var(--relationship-muted, #59635f); font-size: var(--ds-font-body-sm); }
/* Neutral surface: severity is the icon's job, not a tinted strip. */
.relationship-history-panel__problem { display: flex; align-items: center; flex-wrap: wrap; gap: var(--ds-space-sm); padding: var(--ds-space-md); color: var(--relationship-muted, #59635f); font-size: var(--ds-font-body-sm); }
.relationship-history-panel__problem .v-icon { color: var(--relationship-muted, #59635f); }
.relationship-history-panel__problem--failure .v-icon { color: var(--v-error-base); }
.relationship-history-panel__list .relationship-history-panel__problem { border-bottom: 1px solid var(--ds-border-subtle); }
.relationship-history-panel__problem span { min-width: 0; flex: 1; }
.relationship-history-panel ::v-deep .v-btn { min-height: 44px; }

@media (prefers-reduced-motion: reduce) {
  .relationship-history-panel *, .relationship-history-panel *::before, .relationship-history-panel *::after { transition-duration: 0.01ms !important; animation-duration: 0.01ms !important; }
}
</style>
